import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { Button, Fish, PageHead, Seg, Switch, Tabs } from "@ca/ui";
import { NewBadge } from "./flags.tsx";
import { useRevisionStudy } from "./use-study.ts";
import { Failed, mediaUrl, useRead, useWrite } from "./revision-ui.tsx";
import type { RevisionProps } from "./revision-theory.tsx";
const Item = z.object({
  id: z.uuid(),
  part: z.number(),
  lines: z.number(),
  test: z.string(),
  audioKey: z.string().nullable(),
  locked: z.boolean(),
});
const Detail = z.object({
  id: z.uuid(),
  part: z.number(),
  audioKey: z.string().nullable(),
  lines: z.array(
    z.object({
      id: z.uuid(),
      lineNo: z.number(),
      speaker: z.string().nullable(),
      clipKey: z.string().nullable(),
      wordCount: z.number(),
      hints: z.array(z.string()),
    }),
  ),
});
const Result = z.object({
  score: z.number(),
  total: z.number(),
  results: z.array(
    z.object({
      id: z.uuid(),
      text: z.string(),
      translation: z.string().nullable(),
      extra: z.number(),
      words: z.array(z.object({ word: z.string(), correct: z.boolean() })),
    }),
  ),
});
type Graded = z.infer<typeof Result>;
/** Nghe & chép — `S.dict` của bản mẫu (spec 06): chọn đoạn bên phải, chép từng câu bên trái. */
export function Dictation(props: RevisionProps) {
  const [part, setPart] = useState(1);
  const [offset, setOffset] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [result, setResult] = useState<Graded | null>(null);
  const q = useRead(props.uid, `/v1/dictation?part=${part}&offset=${offset}`, z.array(Item));
  const item = q.data?.find((x) => x.id === picked) ?? q.data?.[0];
  const detail = useRead(props.uid, `/v1/dictation/${item?.id ?? ""}`, Detail, !!item && started);
  const pct = (lineId: string) => {
    const r = result?.results.find((x) => x.id === lineId);
    if (!r?.words.length) return null;
    return Math.round((r.words.filter((w) => w.correct).length / r.words.length) * 100);
  };
  return (
    <>
      <PageHead
        space="study"
        title={
          <>
            Nghe & chép <NewBadge />
          </>
        }
        desc="Nghe từng câu rồi chép lại, Cá chấm từng từ. Giờ học chỉ bắt đầu tính khi bạn bấm “Bắt đầu chép”."
      >
        <Tabs
          label="Chọn part"
          items={[1, 2, 3, 4].map((p) => ({ key: String(p), label: `Part ${p}` }))}
          active={String(part)}
          onChange={(k) => {
            setPart(Number(k));
            setOffset(0);
            setPicked(null);
            setStarted(false);
            setResult(null);
          }}
        />
      </PageHead>
      <Failed error={q.error} />
      <div className="grid g-8-4">
        <section className="card stack">
          {item ? (
            <>
              <div className="row between">
                <b>
                  {item.test} · Part {item.part} · {item.lines} câu
                </b>
                <span className="chip">Mức: chép cả câu</span>
              </div>
              {item.locked ? (
                <div className="startbox">
                  <b>🔒 Đoạn này thuộc gói Pro</b>
                  <p className="muted" style={{ fontSize: 13.5 }}>
                    Bản miễn phí mở một số part. Nâng cấp để chép mọi đoạn nghe.
                  </p>
                  <a className="btn btn-acc btn-sm" style={{ alignSelf: "flex-start" }} href="/pro">
                    Mở khoá Pro
                  </a>
                </div>
              ) : !started ? (
                <div className="startbox">
                  <div className="row" style={{ gap: 14, flexWrap: "nowrap" }}>
                    <div style={{ width: 84, flex: "none" }}>
                      <Fish />
                    </div>
                    <div>
                      <b style={{ fontSize: 16 }}>Sẵn sàng chép đoạn này?</b>
                      <p className="muted" style={{ fontSize: 13.5 }}>
                        {item.lines} câu. Giờ học chỉ bắt đầu tính khi bạn bấm “Bắt đầu chép”, xem
                        danh sách thì chưa tính.
                      </p>
                    </div>
                  </div>
                  <Button
                    size="lg"
                    style={{ alignSelf: "flex-start" }}
                    onClick={() => {
                      setStarted(true);
                      setResult(null);
                    }}
                  >
                    ▶ Bắt đầu chép
                  </Button>
                </div>
              ) : detail.data ? (
                <DictationWork
                  key={detail.data.id}
                  {...props}
                  detail={detail.data}
                  result={result}
                  setResult={setResult}
                  stop={() => {
                    setStarted(false);
                  }}
                />
              ) : (
                <Failed error={detail.error} />
              )}
            </>
          ) : (
            <p className="faint">
              {q.isPending ? "Đang tải…" : "Chưa có đoạn nghe chép ở part này."}
            </p>
          )}
        </section>
        <section className="card">
          {started && detail.data ? (
            <>
              <h3 style={{ marginBottom: 8 }}>Các câu trong đoạn</h3>
              {detail.data.lines.map((l, i) => {
                const v = pct(l.id);
                return (
                  <div key={l.id} className="list-row">
                    <span className={`chip${v === null ? "" : v === 100 ? " good" : " warn"}`}>
                      {i + 1}
                    </span>
                    <span className="grow" style={{ fontSize: 13.5 }}>
                      {result?.results.find((r) => r.id === l.id)?.text ??
                        `${l.speaker ? `${l.speaker} · ` : ""}${l.wordCount} từ`}
                    </span>
                    <span className="faint" style={{ fontSize: 12, fontWeight: 700 }}>
                      {v === null ? "chưa làm" : `${v}%`}
                    </span>
                  </div>
                );
              })}
            </>
          ) : (
            <>
              <h3 style={{ marginBottom: 8 }}>Các đoạn Part {part}</h3>
              {q.data?.map((x, i) => (
                <button
                  key={x.id}
                  className="list-row"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    borderRadius: 10,
                    background: x.id === item?.id ? "var(--accent-soft)" : undefined,
                  }}
                  aria-pressed={x.id === item?.id}
                  onClick={() => {
                    setPicked(x.id);
                    setStarted(false);
                    setResult(null);
                  }}
                >
                  <span className={`chip${x.id === item?.id ? " acc" : ""}`}>{offset + i + 1}</span>
                  <span className="grow" style={{ fontSize: 13.5 }}>
                    {x.test}
                  </span>
                  <span className="faint" style={{ fontSize: 12, fontWeight: 700 }}>
                    {x.locked ? "🔒 Pro" : `${x.lines} câu`}
                  </span>
                </button>
              ))}
              {(offset > 0 || (q.data?.length ?? 0) >= 40) && (
                <div className="row" style={{ justifyContent: "center", marginTop: 8 }}>
                  <Button
                    variant="soft"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => {
                      setOffset(Math.max(0, offset - 40));
                    }}
                  >
                    ‹ Trước
                  </Button>
                  <Button
                    variant="soft"
                    size="sm"
                    disabled={(q.data?.length ?? 0) < 40}
                    onClick={() => {
                      setOffset(offset + 40);
                    }}
                  >
                    Sau ›
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
}
function DictationWork({
  uid,
  notify,
  detail,
  result,
  setResult,
  stop,
}: RevisionProps & {
  detail: z.infer<typeof Detail>;
  result: Graded | null;
  setResult: (r: Graded | null) => void;
  stop: () => void;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [hints, setHints] = useState<Record<string, number>>({});
  const [speed, setSpeed] = useState(1);
  const [loop, setLoop] = useState(false);
  const [busy, setBusy] = useState(false);
  const audio = useRef<HTMLAudioElement>(null);
  const write = useWrite(uid, notify);
  const settle = useRevisionStudy(uid, "dictation", detail.id, "dict");
  async function grade() {
    if (busy) return;
    setBusy(true);
    try {
      await settle();
      setResult(
        Result.parse(
          await write(`/v1/me/dictation/${detail.id}`, "PUT", {
            answers: detail.lines.map((l) => ({ id: l.id, text: answers[l.id] ?? "" })),
          }),
        ),
      );
    } catch {
      /* The write helper reports failure and the form keeps its current contents. */
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <StudyChip stop={stop} />
      <div className="row" style={{ gap: 8 }}>
        {detail.audioKey && (
          <audio ref={audio} controls loop={loop} src={mediaUrl(detail.audioKey)} />
        )}
        <Seg
          label="Tốc độ"
          options={[0.75, 1, 1.25].map((n) => ({ value: n, label: `${n}×` }))}
          value={speed}
          onChange={(value) => {
            setSpeed(value);
            if (audio.current) audio.current.playbackRate = value;
            document.querySelectorAll<HTMLAudioElement>("[data-dictation-audio]").forEach((a) => {
              a.playbackRate = value;
            });
          }}
        />
        <span className="row" style={{ gap: 6 }}>
          🔁 Lặp <Switch label="Lặp âm thanh" checked={loop} onChange={setLoop} />
        </span>
      </div>
      {detail.lines.map((line) => {
        const r = result?.results.find((x) => x.id === line.id);
        return (
          <div
            key={line.id}
            className="stack"
            style={{ gap: 8, borderTop: "1px solid var(--line)", paddingTop: 12 }}
          >
            <div className="row between">
              <b style={{ fontSize: 14 }}>
                Câu {line.lineNo + 1}
                {line.speaker ? ` · ${line.speaker}` : ""}
              </b>
              <span className="faint" style={{ fontSize: 12, fontWeight: 700 }}>
                {line.wordCount} từ
              </span>
            </div>
            {line.clipKey && (
              <audio
                data-dictation-audio
                controls
                loop={loop}
                src={mediaUrl(line.clipKey)}
                onLoadedMetadata={(e) => {
                  e.currentTarget.playbackRate = speed;
                }}
              />
            )}
            <div className="field">
              <label htmlFor={`dict-${line.id}`}>Bạn nghe được gì?</label>
              <textarea
                id={`dict-${line.id}`}
                className="input"
                rows={2}
                aria-label={`Chép dòng ${line.lineNo + 1}`}
                placeholder="Gõ lại câu vừa nghe…"
                value={answers[line.id] ?? ""}
                onChange={(e) => {
                  setAnswers({ ...answers, [line.id]: e.target.value });
                  setResult(null);
                }}
              />
            </div>
            <div className="row" style={{ gap: 6 }}>
              {[1, 2, 3].map((level) => (
                <Button
                  variant={(hints[line.id] ?? 0) === level ? "dark" : "soft"}
                  size="sm"
                  key={level}
                  onClick={() => {
                    setHints({ ...hints, [line.id]: (hints[line.id] ?? 0) === level ? 0 : level });
                  }}
                >
                  Gợi ý {level}
                </Button>
              ))}
              {(hints[line.id] ?? 0) > 0 && (
                <span className="chip acc">{line.hints[(hints[line.id] ?? 1) - 1]}</span>
              )}
            </div>
            {r && (
              <div className="explain">
                <div className="diff">
                  {r.words.map((w, i) => (
                    <span key={i} className={w.correct ? "ok" : "no"}>
                      {w.word}{" "}
                    </span>
                  ))}
                </div>
                {r.translation && <p className="muted">{r.translation}</p>}
                {r.extra > 0 && <p className="faint">{r.extra} từ thừa hoặc không khớp.</p>}
              </div>
            )}
          </div>
        );
      })}
      <div className="row">
        <Button variant="acc" disabled={busy} onClick={() => void grade()}>
          Chấm từng từ
        </Button>
        {result && (
          <span role="status" className={`chip ${result.score === result.total ? "good" : "warn"}`}>
            Đúng {result.score}/{result.total} từ ·{" "}
            {result.total ? Math.round((result.score / result.total) * 100) : 0}%
          </span>
        )}
      </div>
    </>
  );
}
/** Thanh "đang tính giờ học · ■ Xong phiên" (`sessBar` của bản mẫu). */
function StudyChip({ stop }: { stop: () => void }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const update = (e: Event) => {
      setOn((e as CustomEvent<{ state: string }>).detail.state === "recording");
    };
    window.addEventListener("ca-study-status", update);
    return () => {
      window.removeEventListener("ca-study-status", update);
    };
  }, []);
  return (
    <div className={`sessbar${on ? "" : " paused"}`}>
      <span className="ss-dot" />
      <span className="ss-lbl">{on ? "đang tính giờ học" : "tạm dừng tính giờ"}</span>
      <span className="grow" />
      <Button variant="soft" size="sm" onClick={stop}>
        ■ Xong phiên
      </Button>
    </div>
  );
}
